import * as React from 'react'
import { theme } from '../theme'

interface BrushSizeControlProps {
	brushSize: number
	onBrushSizeChange: (size: number) => void
}

/**
 * Custom CSS for range input styling across browsers.
 * Provides consistent look for the brush size slider.
 */
const sliderStyles = `
  .brush-size-control input[type=range] {
    -webkit-appearance: none;
    width: 100%;
    height: 4px;
    background-color: rgba(0, 102, 255, 0.1);
    outline: none;
    border-radius: 2px;
    cursor: pointer;
  }

  .brush-size-control input[type=range]::-webkit-slider-thumb {
    -webkit-appearance: none;
    width: 20px;
    height: 20px;
    background-color: ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  .brush-size-control input[type=range]::-webkit-slider-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .brush-size-control input[type=range]::-moz-range-thumb {
    width: 20px;
    height: 20px;
    background-color: ${theme.colors.primary};
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
    transition: all 0.2s ease;
  }

  .brush-size-control input[type=range]::-moz-range-thumb:hover {
    transform: scale(1.1);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.2);
  }

  .brush-size-control input[type=range]::-moz-range-track {
    background-color: rgba(0, 102, 255, 0.1);
    height: 4px;
    border-radius: 2px;
  }
`

/**
 * Brush size control component with slider input.
 * Allows adjusting brush size between 20px and 150px.
 *
 * @param {number} brushSize - Current brush size in pixels
 * @param {Function} onBrushSizeChange - Handler for brush size changes
 */
export const BrushSizeControl: React.FC<BrushSizeControlProps> = ({ brushSize, onBrushSizeChange }) => {
	// Add custom slider styles to document head
	React.useEffect(() => {
		const styleSheet = document.createElement('style')
		styleSheet.innerText = sliderStyles
		document.head.appendChild(styleSheet)

		// Cleanup styles on unmount
		return () => {
			document.head.removeChild(styleSheet)
		}
	}, [])

	return (
		<div
			className="brush-size-control"
			style={{
				display: 'flex',
				alignItems: 'center',
				gap: theme.spacing.md,
				width: '100%',
			}}
		>
			{/* Brush size label */}
			<div
				style={{
					fontSize: '14px',
					color: theme.colors.text.primary,
					whiteSpace: 'nowrap',
					minWidth: '90px',
				}}
			>
				Brush size: <span style={{ color: theme.colors.primary }}>{brushSize}px</span>
			</div>

			{/* Brush size slider */}
			<div style={{ flex: 1, padding: `${theme.spacing.sm}px ${theme.spacing.md}px` }}>
				<input
					type="range"
					min="50"
					max="150"
					value={brushSize}
					onChange={(e) => onBrushSizeChange(parseInt(e.target.value, 10))}
					style={{ width: '100%' }}
				/>
			</div>
		</div>
	)
}
